import { BusinessObjectBase } from './base';
import type { Organization } from './organization';
import type { Placement } from './placement';
import type { StaffType } from './staff-type';
import type { Venue } from './venue';

export interface PositionData {
  readonly id: string;
  readonly name?: string;
  readonly staffTypeId: number;
  readonly staffType?: StaffType;
  readonly venueId: number;
  readonly venue?: Venue;
  readonly organizationId: number;
  readonly organization?: Organization;
  readonly placements?: Placement[];
}

export interface Position extends Readonly<PositionData> {}
export class Position extends BusinessObjectBase {
  static readonly entityName = 'Position' as const;
  static readonly plural = 'positions';

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Position[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Position(data));
  }

  static async findOne(filter?: any): Promise<Position | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Position(data) : null;
  }

  static async findById(id: string, filter?: any): Promise<Position> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Position(data);
  }

  static async create(data: Omit<PositionData, 'id'>): Promise<Position> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Position(result);
  }

  static async updateById(
    id: string,
    data: Partial<PositionData>,
  ): Promise<Position> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const result = await this.patch<any>(url, data);
    return new Position(result);
  }

  static async deleteById(id: string): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
